import { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { z } from 'zod'
import type { Auth0Role } from '~/config/auth'
import { ROLE_CLAIM_KEY, CUSTOMER_CLAIM_KEY } from '~/config/auth'
import { useStore } from '~/state/useStore'

const claimsSchema = z.object({
  sub: z.string(),
  email: z.string().email(),
  name: z.string(),
  picture: z.string().url().optional(),
  [ROLE_CLAIM_KEY]: z.enum(['app', 'app_admin']),
  [CUSTOMER_CLAIM_KEY]: z.string().min(1),
})

export function useSyncAuthToStore() {
  const { user, isAuthenticated } = useAuth0()
  const setUser = useStore((s) => s.auth.setUser)
  const clearUser = useStore((s) => s.auth.clear)

  useEffect(() => {
    if (!isAuthenticated || !user) {
      clearUser()
      return
    }
    const parsed = claimsSchema.safeParse(user)
    if (!parsed.success) {
      console.error('[auth] missing required claims; denying session', parsed.error)
      clearUser()
      return
    }
    // Cast to unknown first to work around zod's handling of computed property names
    const data = parsed.data as unknown as Record<string, unknown>
    const role = data[ROLE_CLAIM_KEY] as Auth0Role
    const customer = data[CUSTOMER_CLAIM_KEY] as string
    setUser({
      sub: data.sub as string,
      email: data.email as string,
      name: data.name as string,
      picture: data.picture as string | undefined,
      role,
      customer,
    })
  }, [isAuthenticated, user, setUser, clearUser])
}
